import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { useRestaurant } from '@/contexts/RestaurantContext';
import { useUserRole } from '@/hooks/useUserRole';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { subscription, isLoading: subscriptionLoading, isPendingApproval, isRejected, isExpired, isSuspended, canAccessDashboard } = useSubscription();
  const { currentRestaurant, isLoading: restaurantLoading } = useRestaurant();
  const { isSupport, isMasterAdmin, isLoading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();

  // SECURITY: Block support users from accessing client routes
  // Support users should ONLY access /internal-support-* routes
  const isSupportOnClientRoute = useMemo(() => {
    if (!isSupport) return false;
    const clientRoutes = ['/dashboard', '/sales', '/inventory', '/menu', '/costs', '/reports', '/settings', '/stock', '/categories'];
    return clientRoutes.some(route => location.pathname.startsWith(route));
  }, [isSupport, location.pathname]);

  // Calculate redirect path once to avoid flickering
  // DENY BY DEFAULT: If we can't confirm access, block.
  const redirectPath = useMemo(() => {
    const currentPath = location.pathname;
    // Routes that should not trigger redirects
    const skipPaths = ['/subscription', '/settings', '/checkout', '/checkout-success', '/approval-success', '/pending-approval', '/access-denied', '/account-suspended', '/onboarding-wizard'];
    
    // Already on a special page, don't redirect
    if (skipPaths.includes(currentPath)) return null;

    // SECURITY FIX: If subscription is null after loading, deny access
    if (!subscription) {
      return '/pending-approval';
    }
    
    // Priority 0: Suspended account
    if (isSuspended) {
      return '/account-suspended';
    }

    // Priority 1: Pending approval
    if (isPendingApproval) {
      return '/pending-approval';
    }
    
    // Priority 2: Rejected users
    if (isRejected) return '/access-denied';
    
    // Priority 3: Expired subscription/trial
    if (isExpired) {
      return '/subscription';
    }
    
    // Priority 4: First login after approval
    if (
      subscription.approval_status === 'approved' && 
      subscription.first_login_after_approval === true &&
      isMasterAdmin
    ) {
      return '/approval-success';
    }

    // Priority 5: Can access dashboard - ALLOW
    if (canAccessDashboard) {
      return null;
    }
    
    // DEFAULT: Deny access
    return '/pending-approval';
  }, [subscription, isPendingApproval, isRejected, isExpired, isSuspended, canAccessDashboard, location.pathname]);

  // Track if user was ever authenticated in this mount cycle
  const hasBeenAuthenticated = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (user) {
      hasBeenAuthenticated.current = true;
    }
  }, [user]);

  // Redirect to auth if not logged in - WITH DEBOUNCE for token refresh
  useEffect(() => {
    if (!authLoading && !user) {
      if (hasBeenAuthenticated.current) {
        // Grace period: wait for token refresh to complete before redirecting
        const timer = setTimeout(() => {
          if (!userRef.current) {
            navigate('/auth');
          }
        }, 1500);
        return () => clearTimeout(timer);
      } else {
        // Never authenticated in this session = go to auth immediately
        navigate('/auth');
      }
    }
  }, [user, authLoading, navigate]);

  // SECURITY: Redirect support users away from client routes
  useEffect(() => {
    if (!authLoading && !roleLoading && isSupportOnClientRoute) {
      navigate('/internal-support-alc2024/dashboard', { replace: true });
    }
  }, [authLoading, roleLoading, isSupportOnClientRoute, navigate]);

  // Handle subscription-based redirects
  useEffect(() => {
    if (!authLoading && !subscriptionLoading && !restaurantLoading && user && redirectPath && !isSupport) {
      navigate(redirectPath, { replace: true });
    }
  }, [authLoading, subscriptionLoading, restaurantLoading, user, redirectPath, navigate, isSupport]);

  // Unified loading state - show loader while auth OR subscription OR restaurant OR role is loading
  const isLoading = authLoading || (user && (subscriptionLoading || restaurantLoading || roleLoading));

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Show loading while redirecting to prevent flash
  if (redirectPath || isSupportOnClientRoute) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="text-muted-foreground text-sm">Redirecionando...</p>
      </div>
    );
  }

  return <>{children}</>;
};
